import { useEffect, useRef } from 'react'

import { computeBundleBytes } from '../utils/computeBundleBytes'
import { computeCacheStateForScripts } from '../utils/computeCacheStateForScripts'

export const useDashboardLoadMetrics = (opts: { isFetchingOrganizationSettings: boolean }) => {
  const { isFetchingOrganizationSettings } = opts

  const dataReadySentRef = useRef(false)
  const shellMsRef = useRef<number | null>(null)
  const cacheStateRef = useRef<string | null>(null)
  const isAppBootRef = useRef(false)

  useEffect(() => {
    const dataDog = window.DD_RUM
    const consume = window.__metrics__?.app_boot_consumed === false

    if (!consume) {
      isAppBootRef.current = false
      return
    }

    isAppBootRef.current = true

    if (window.__metrics__) {
      window.__metrics__.app_boot_consumed = true
    }

    const { cacheState } = computeCacheStateForScripts()
    cacheStateRef.current = cacheState

    shellMsRef.current = performance.now()

    if (dataDog && typeof dataDog.addTiming === 'function') {
      dataDog.addTiming('dashboard_shell_ready')
    }

    if (dataDog && typeof dataDog.addAction === 'function') {
      dataDog.addAction('dashboard_load_metrics', {
        phase: 'shell_ready',
        /* eslint-disable @typescript-eslint/naming-convention */
        entry_type: 'app_boot',
        cache_state: cacheState,
        shell_ms: shellMsRef.current == null ? null : Math.round(shellMsRef.current),
        /* eslint-enable @typescript-eslint/naming-convention */
      })
    }
  }, [])

  useEffect(() => {
    if (!isAppBootRef.current || dataReadySentRef.current || isFetchingOrganizationSettings) {
      return
    }

    const dataDog = window.DD_RUM

    dataReadySentRef.current = true

    const dataMs = performance.now()
    const shellMs = shellMsRef.current
    const apiMs = shellMs != null ? Math.max(0, dataMs - shellMs) : null
    const cacheState = cacheStateRef.current

    if (dataDog && typeof dataDog.addTiming === 'function') {
      dataDog.addTiming('dashboard_data_ready')
    }

    const bundle = computeBundleBytes({ maxStartTime: dataMs, pathPrefixes: ['/releases/'] })

    if (dataDog && typeof dataDog.addAction === 'function') {
      dataDog.addAction('dashboard_load_metrics', {
        phase: 'data_ready',
        /* eslint-disable @typescript-eslint/naming-convention */
        entry_type: 'app_boot',
        cache_state: cacheState,
        shell_ms: shellMs == null ? null : Math.round(shellMs),
        data_ms: dataMs == null ? null : Math.round(dataMs),
        api_ms: apiMs == null ? null : Math.round(apiMs),

        bundle_script_count: bundle.scriptCount,
        bundle_transfer_bytes: bundle.transferBytes,
        bundle_encoded_bytes: bundle.encodedBytes,
        /* eslint-enable @typescript-eslint/naming-convention */
      })
    }
  }, [isFetchingOrganizationSettings])
}
